import { Formik, Form as FormikForm, useField } from "formik";
import React, { useState } from "react";
import { Alert } from "components/ui/BootstrapCompat";
import AuthPageFrame from "components/AuthPageFrame";
import { useNavigate } from "react-router-dom";
import { authForgotPasswordSendOtp } from "services/main/public.service";

type ForgotPasswordValues = {
  identifier: string;
};

const initialValues: ForgotPasswordValues = {
  identifier: "",
};

const getErrorMessage = (error: any) => {
  return (
    error?.response?.data?.message ||
    error?.message ||
    "ເກີດຂໍ້ຜິດພາດ ກະລຸນາລອງໃໝ່ອີກຄັ້ງ"
  );
};

const RECOVERY_CHANNEL = "email";

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [statusMessage, setStatusMessage] = useState("");
  const [statusVariant, setStatusVariant] = useState<"success" | "danger">(
    "success"
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  return (
    <AuthPageFrame backLabel="ກັບໄປ Login" onBack={() => navigate("/login")}>
      <header className="auth-heading">
        <h1>ລືມລະຫັດຜ່ານ</h1>
        <p>ປ້ອນ username ຫຼື email ເພື່ອຮັບ OTP ສຳລັບຕັ້ງລະຫັດຜ່ານໃໝ່</p>
      </header>

      <Formik
        initialValues={initialValues}
        validate={(values) => {
          const errors: Record<string, string> = {};
          if (!values.identifier.trim()) {
            errors.identifier = "ກະລຸນາປ້ອນ username ຫຼື email";
          }
          return errors;
        }}
        validateOnMount
        onSubmit={async (values, { resetForm }) => {
          setIsSubmitting(true);
          setStatusMessage("");
          try {
            const identifier = values.identifier.trim();
            await authForgotPasswordSendOtp({
              identifier,
              channel: RECOVERY_CHANNEL,
            });
            setStatusVariant("success");
            setStatusMessage(
              "ສົ່ງ OTP ໄປທີ່ email ແລ້ວ ກະລຸນານຳ OTP ໄປຕັ້ງລະຫັດຜ່ານໃໝ່",
            );
            navigate(
              `/reset-password?identifier=${encodeURIComponent(identifier)}`,
            );
            resetForm();
          } catch (error: any) {
            setStatusVariant("danger");
            setStatusMessage(getErrorMessage(error));
          } finally {
            setIsSubmitting(false);
          }
        }}
      >
        {({ isValid }) => (
          <FormikForm className="auth-register-form">
            <div className="auth-field-stack">
              <RecoveryInput
                label="Username ຫຼື Email"
                name="identifier"
                type="text"
                placeholder="streamer01 ຫຼື example@gmail.com"
                autoComplete="username"
              />
            </div>

            {statusMessage && (
              <Alert
                variant={statusVariant}
                className="auth-alert mb-0 mt-4 border-0"
              >
                {statusMessage}
              </Alert>
            )}

            <button
              type="submit"
              disabled={!isValid || isSubmitting}
              className="auth-submit-button"
            >
              {isSubmitting ? "ກຳລັງສົ່ງ OTP..." : "ສົ່ງ OTP"}
            </button>
          </FormikForm>
        )}
      </Formik>
    </AuthPageFrame>
  );
}

type RecoveryInputProps = React.InputHTMLAttributes<HTMLInputElement> & {
  label: string;
  name: keyof ForgotPasswordValues;
};

function RecoveryInput({
  label,
  name,
  className = "",
  ...props
}: RecoveryInputProps) {
  const [field, meta] = useField(name);
  const showError = Boolean(meta.touched && meta.error);

  return (
    <label className="auth-field" htmlFor={name}>
      <span>{label}</span>
      <input
        {...field}
        {...props}
        id={name}
        name={name}
        aria-invalid={showError}
        className={`auth-control ${showError ? "is-invalid" : ""} ${className}`.trim()}
      />
      {showError && <small>{meta.error}</small>}
    </label>
  );
}
